import type { SummaryBooked } from "@planerius/contracts/cabinet"
import { type ReactNode, useCallback, useEffect, useState } from "react"

import { useHasPermission } from "../../access/use-access"
import { type ApiProblem, isApiProblem } from "../../api/problem"
import { fetchBooked } from "../../api/summary.api"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { Button, EmptyState } from "../../ui"
import { formatMoney } from "../directories/format-money"
import { StandHeader } from "../directories/stand"
import {
  BOOKED_DEGRADED,
  BOOKED_FOOTNOTE,
  BOOKED_PERIODS,
  type BookedPeriod,
  bookedCaption,
  comparisonText,
  dayRowCaption,
  dayRowMoney,
  dayRowTitle,
  emptyText,
  horizonText,
  occupancyText,
  periodLabel,
  receivedCaption,
} from "./booked-model"
import styles from "./summary-screen.module.css"

/**
 * 5-В4 «Записано» — /summary/booked?days=7.
 *
 * ЭКРАН ОТВЕЧАЕТ НА ОДИН ВОПРОС: сколько денег уже стоит в будущих записях. И отвечает
 * так, чтобы ответ нельзя было прочитать как обещание: слова «прогноз» здесь нет,
 * а под суммой стоит, что с ней случится дальше — отмены уменьшат, новые записи
 * увеличат.
 *
 * ДВЕ ЦИФРЫ РАЗВЕДЕНЫ ФОРМОЙ. «Записано · предстоит» — пунктирная рамка: этих денег
 * ещё нет. «Получено · состоялось» — заливка: эти уже есть. Владелец, глядя на экран
 * секунду между клиентами, обязан различить их не читая.
 *
 * ПУСТОЙ ПЕРИОД ОБЪЯСНЯЕТСЯ СЛОВАМИ, А НЕ НУЛЁМ. «0,00 ₽» читается как провал продаж
 * и кончается разговором с администратором, который ни при чём: чаще всего причина
 * в том, что смен нет или горизонт записи закрыт.
 */
export function SummaryBookedScreen(props: {
  readonly search: { readonly days?: number | undefined; readonly branch?: string | undefined }
  readonly onSearchChange: (next: { days?: number | undefined }) => void
  readonly onBack: () => void
  readonly onOpenDay: (date: string) => void
  /** Выбран один филиал: только тогда день открывается в журнале однозначно. */
  readonly singleBranch: boolean
}): ReactNode {
  const mayOpenJournal = useHasPermission("schedule.view")
  const [booked, setBooked] = useState<SummaryBooked | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)

  const days = (BOOKED_PERIODS as readonly number[]).includes(props.search.days ?? 7)
    ? ((props.search.days ?? 7) as BookedPeriod)
    : 7
  const { branch } = props.search

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const next = await fetchBooked(
          { days, ...(branch === undefined ? {} : { branchId: branch }) },
          signal,
        )
        if (signal?.aborted === true) return
        setBooked(next)
        setProblem(null)
      } catch (error) {
        if (signal?.aborted === true) return
        if (!isApiProblem(error)) throw error
        // ЦИФРЫ ПРЕЖНЕГО ПЕРИОДА ПОД НОВОЙ ПОДПИСЬЮ — ЭТО ЛОЖЬ. Человек нажал
        // «14 дней», запрос не прошёл, а на экране остались суммы за семь под
        // выбранным чипом «14 дней»: он прочитает их как двухнедельные.
        setBooked(null)
        setProblem(error)
      }
    },
    [days, branch],
  )

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  if (problem !== null && booked === null) {
    return <ProblemScreen problem={problem} onRetry={() => void load()} />
  }

  const empty = booked === null ? null : emptyText(booked)
  const horizon = booked === null ? null : horizonText(booked)
  const comparison = booked === null ? null : comparisonText(booked)
  const occupancy = booked === null ? null : occupancyText(booked)

  return (
    <div className={styles["screen"]}>
      <StandHeader title="Записано">
        <Button size="sm" variant="secondary" onClick={props.onBack}>
          ‹ Сводка
        </Button>
      </StandHeader>

      <div className={styles["chips"]} role="group" aria-label="Период">
        {BOOKED_PERIODS.map((value) => (
          <Button
            key={value}
            size="sm"
            variant={value === days ? "primary" : "secondary"}
            aria-pressed={value === days}
            onClick={() => {
              props.onSearchChange({ days: value })
            }}
          >
            {periodLabel(value)}
          </Button>
        ))}
      </div>

      {booked === null ? (
        <div className={styles["skeletons"]} aria-busy="true" aria-live="polite">
          {[0, 1].map((row) => (
            <Skeleton key={row} width="100%" height={92} radius={12} />
          ))}
        </div>
      ) : booked.bookedMinor === null ? (
        <p className={styles["caption"]}>{BOOKED_DEGRADED}</p>
      ) : (
        <>
          <div className={styles["cards"]}>
            {/* Пунктир: этих денег ЕЩЁ НЕТ. */}
            <div className={styles["cardAhead"]}>
              <span className={styles["cardTitle"]}>Записано · предстоит</span>
              <span className={styles["value"]}>{formatMoney(booked.bookedMinor) ?? "—"}</span>
              <span className={styles["caption"]}>{bookedCaption(booked)}</span>
            </div>

            {/* Заливка: эти деньги УЖЕ ЕСТЬ. */}
            <div className={styles["cardDone"]}>
              <span className={styles["cardTitle"]}>Получено · состоялось</span>
              <span className={styles["value"]}>
                {booked.receivedMinor === null ? "—" : (formatMoney(booked.receivedMinor) ?? "—")}
              </span>
              <span className={styles["caption"]}>{receivedCaption(booked)}</span>
            </div>
          </div>

          <p className={styles["footnote"]}>{BOOKED_FOOTNOTE}</p>

          {occupancy === null ? null : (
            <div className={styles["card"]}>
              <span className={styles["cardTitle"]}>Занято времени смен</span>
              <span className={styles["value"]}>{occupancy}</span>
            </div>
          )}

          {comparison === null ? null : <p className={styles["footnote"]}>{comparison}</p>}

          {empty === null ? null : (
            <EmptyState title="Записей на этот период нет" text={empty} />
          )}

          {empty !== null ? null : (
            <div className={styles["rows"]}>
              {booked.rows.map((row) => {
                const body = (
                  <>
                    <span className={styles["rowHead"]}>
                      <span className={styles["rowName"]}>{dayRowTitle(row, booked.timezone)}</span>
                      <span className={styles["rowValue"]}>{dayRowMoney(row)}</span>
                    </span>
                    <span className={styles["rowCaption"]}>{dayRowCaption(row)}</span>
                  </>
                )
                /*
                 * ЖУРНАЛ ПОКАЗЫВАЕТ ОДИН ФИЛИАЛ. В режиме «все филиалы» строка дня
                 * складывает точки, и открыть её значит показать день ОДНОЙ из них
                 * под суммой всех: число на экране журнала не сойдётся со строкой,
                 * из которой человек в него пришёл.
                 */
                return mayOpenJournal && props.singleBranch ? (
                  <button
                    key={row.date}
                    type="button"
                    className={styles["row"]}
                    onClick={() => {
                      props.onOpenDay(row.date)
                    }}
                  >
                    {body}
                  </button>
                ) : (
                  <div key={row.date} className={styles["rowStatic"]}>
                    {body}
                  </div>
                )
              })}
            </div>
          )}

          {horizon === null ? null : <p className={styles["footnote"]}>{horizon}</p>}
        </>
      )}
    </div>
  )
}
